import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/Button';
import Logo from './Logo';
import { landingContainerClass } from './landing/LandingContainer';
import {
  Inbox,
  Globe,
  Settings,
  User,
  LogOut,
  Menu,
  X,
  ChevronDown,
  BookOpen,
  Home,
  CreditCard,
  LifeBuoy
} from 'lucide-react';

// Navigation configuration
const NAVIGATION_ITEMS = [
  { to: '/dashboard', icon: Inbox, label: 'Inbox' },
  { to: '/endpoints', icon: Globe, label: 'Forms' },
];

const ACCOUNT_LINKS = [
  { to: '/billing', icon: CreditCard, label: 'Billing' },
  { to: '/support', icon: LifeBuoy, label: 'Support' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

const PUBLIC_NAVIGATION_ITEMS = [
  { to: '/', icon: Home, label: 'Home' },
  { to: '/blog', icon: BookOpen, label: 'Blog' },
  { to: '/docs', icon: BookOpen, label: 'Docs' },
];

// Navigation Link Component
const NavLink = ({ to, children, icon: Icon, className = "", onClick }) => {
  const location = useLocation();
  const isActive = to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);
  
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 h-10 rounded-xl text-sm font-semibold transition-colors ${
        isActive 
          ? 'bg-primary-50 text-primary-800 border border-primary-100' 
          : 'text-charcoal-600 hover:text-charcoal-900 hover:bg-neutral-100'
      } ${className}`}
    >
      {Icon && <Icon className="h-4 w-4" />}
      {children}
    </Link>
  );
};

// User Menu Dropdown Component
const UserMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsOpen(false);
  };

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  const menuItems = [
    ...ACCOUNT_LINKS.map((item) => ({ ...item, onClick: closeMenu })),
    { onClick: handleLogout, icon: LogOut, label: 'Sign Out', isButton: true },
  ];

  return (
    <div className="relative">
      <button
        onClick={toggleMenu}
        className="flex items-center gap-2 px-3 h-10 rounded-xl text-sm font-semibold text-charcoal-600 hover:text-charcoal-900 hover:bg-neutral-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-100"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <div className="w-8 h-8 bg-neutral-100 rounded-full flex items-center justify-center">
          <User className="h-4 w-4 text-charcoal-600" />
        </div>
        <span className="hidden sm:block max-w-32 truncate">
          {user?.email || 'User'}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={closeMenu} />
          
          {/* Dropdown Menu */}
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl shadow-charcoal-900/10 border border-neutral-200 z-20 overflow-hidden" role="menu">
            <div className="py-1">
              {/* User Info */}
              <div className="px-4 py-2 border-b border-neutral-100">
                <p className="text-sm font-medium text-charcoal-700 truncate">
                  {user?.email || 'User'}
                </p>
              </div>
              
              {/* Menu Items */}
              {menuItems.map((item, index) => (
                item.isButton ? (
                  <button
                    key={index}
                    onClick={item.onClick}
                  className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-charcoal-600 hover:bg-neutral-50 hover:text-charcoal-900 transition-colors"
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </button>
                ) : (
                  <Link
                    key={index}
                    to={item.to}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-charcoal-600 hover:bg-neutral-50 hover:text-charcoal-900 transition-colors"
                    onClick={item.onClick}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                )
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const PublicActions = () => (
  <div className="hidden items-center gap-2 lg:flex">
    <Button asChild variant="ghost" size="sm">
      <Link to="/login">Sign In</Link>
    </Button>
    <Button asChild size="sm">
      <Link to="/register">Start Free</Link>
    </Button>
  </div>
);

// Mobile Menu Component
const MobileMenu = ({ isOpen, onClose, items, isAuthenticated }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
    onClose();
  };

  const handleNavClick = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="lg:hidden">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/15 backdrop-blur-sm z-40" 
        onClick={onClose}
      />
      
      {/* Mobile Menu Panel */}
      <div className="fixed top-0 right-0 z-50 h-full w-[min(18rem,calc(100vw-1rem))] transform bg-white shadow-2xl transition-transform duration-300 ease-in-out">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 bg-white">
          <Logo />
          <button
            onClick={onClose}
            className="p-2 rounded-md text-charcoal-600 hover:text-charcoal-700 hover:bg-neutral-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Navigation */}
        <nav className="p-4 space-y-2 bg-white h-full">
          {items.map((item) => (
            <NavLink 
              key={item.to} 
              to={item.to} 
              icon={item.icon}
              onClick={handleNavClick}
            >
              {item.label}
            </NavLink>
          ))}
          {isAuthenticated ? (
            <>
              <div className="pt-4 mt-2 border-t border-neutral-200 space-y-2">
                {ACCOUNT_LINKS.map((item) => (
                  <NavLink key={item.to} to={item.to} icon={item.icon} onClick={handleNavClick}>
                    {item.label}
                  </NavLink>
                ))}
              </div>
              <div className="pt-4 border-t border-neutral-200">
                <Button 
                  onClick={handleLogout}
                  variant="outline" 
                  className="w-full justify-start h-10"
                >
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </Button>
              </div>
            </>
          ) : (
            <div className="pt-4 border-t border-neutral-200 space-y-2">
              <Button asChild variant="outline" className="w-full justify-center">
                <Link to="/login" onClick={handleNavClick}>Sign In</Link>
              </Button>
              <Button asChild className="w-full justify-center">
                <Link to="/register" onClick={handleNavClick}>Get Started</Link>
              </Button>
            </div>
          )}
        </nav>
      </div>
    </div>
  );
};

// Mobile Menu Toggle Button
const MobileMenuButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="lg:hidden flex h-11 min-h-[44px] min-w-[44px] items-center justify-center rounded-xl p-2 text-charcoal-600 transition-colors hover:bg-neutral-100 hover:text-charcoal-900 touch-manipulation"
    aria-label="Open navigation"
  >
    <Menu className="h-5 w-5" />
  </button>
);

// Desktop Navigation Component
const DesktopNavigation = ({ items }) => (
  <div className="hidden lg:flex items-center space-x-1">
    {items.map((item) => (
      <NavLink key={item.to} to={item.to} icon={item.icon}>
        {item.label}
      </NavLink>
    ))}
  </div>
);

// Main Navbar Component
export default function Navbar() {
  const { isAuthenticated } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigationItems = isAuthenticated ? NAVIGATION_ITEMS : PUBLIC_NAVIGATION_ITEMS;

  // Auth pages use a centered card layout with their own logo.
  const shouldHideNavbar = (
    location.pathname === '/login' || 
    location.pathname === '/register' ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password'
  );

  if (shouldHideNavbar) {
    return null;
  }

  const openMobileMenu = () => setIsMobileMenuOpen(true);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      <nav className="bg-white/85 border-b border-neutral-200/80 sticky top-0 z-30 backdrop-blur-xl">
        <div className={landingContainerClass}>
          <div className="flex h-16 min-w-0 items-center justify-between">
            {/* Left: Logo */}
            <div className="min-w-0 flex-shrink">
              <Logo linkTo={isAuthenticated ? '/dashboard' : '/'} />
            </div>

            {/* Center: Desktop Navigation */}
            <DesktopNavigation items={navigationItems} />

            {/* Right: Actions */}
            <div className="flex flex-shrink-0 items-center gap-2 sm:gap-3">
              {isAuthenticated ? (
                <div className="hidden lg:block">
                  <UserMenu />
                </div>
              ) : (
                <PublicActions />
              )}

              {/* Mobile Menu Button */}
              <MobileMenuButton onClick={openMobileMenu} />
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <MobileMenu 
        isOpen={isMobileMenuOpen} 
        onClose={closeMobileMenu} 
        items={navigationItems}
        isAuthenticated={isAuthenticated}
      />
    </>
  );
} 